
import { Component, Vue, Prop } from "vue-property-decorator";
import { Posting } from "@/generated/graphql";
import BadgeColor from "@/entities/BadgeColor";
import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import { mixins } from "vue-class-component";
import RouteNames from "@/entities/RouteNames";
import ItemChipsData from "@/entities/ItemChipsData";
import Badge from "@/components/common/Badge.vue";
import EditPenIcon from "@/components/icons/EditIcon.vue";
import EyeOffIcon from "@/components/icons/EyeOffIcon.vue";
import getStatusLabel from "@/helpers/postings/getStatusLabel";
import UPDATE_POSTING_ACTIVE_STATUS from "@/apollo/jobboard/UpdatePostingActiveStatus.gql";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import Spinner from "@/components/common/Spinner.vue";
import LocationIcon from "@/components/icons/LocationIconNew.vue";
import getLocationInfo from "@/helpers/locations/getLocationInfo";
import UserIcon from "@/components/icons/UserFill.vue";
import UserProfileIcon from "@/components/icons/UserProfileIcon.vue";
import Calendar from "@/components/icons/CalendarIcon.vue";
import ApplicatoinIcon from "@/components/icons/ApplicantIcon.vue";
import ItemChip from "@/components/common/ItemChip.vue";
import ItemChipNew from "@/components/common/ItemChipNew.vue";
import { getPostedAt } from "@/helpers/postings/getPostedAtLabel";
import PinIcon from "@/components/icons/PinIcon.vue";
import DollarIcon from "@/components/icons/dollar-symbol.vue";
import { formatCurrency } from "@/utils";
import { hslColorFromName } from "@/utils/hslColorFromName";

@Component({
  components: {
    EditPenIcon,
    Badge,
    EyeOffIcon,
    ItemChipNew,
    EyeIcon,
    Spinner,
    LocationIcon,
    UserIcon,
    Calendar,
    ApplicatoinIcon,
    ItemChip,
    UserProfileIcon,
    PinIcon,
    DollarIcon,
  },
})
export default class PostingCardBody extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly posting!: Posting;
  @Prop({ type: Array, required: false, default: [] }) readonly applications!:
    | []
    | ItemChipsData[];
  @Prop({ type: Boolean, default: false }) readonly canEdit!: boolean;
  @Prop({ type: Boolean, default: false }) readonly inactiveManager!: boolean;
  routes = RouteNames;
  processing: boolean = false;
  get getNameClasses() {
    const arr = (this.posting.job_title || "").split(" ");
    const isGreater = arr.find((a) => a.length >= 50);

    return {
      custom_header_class: isGreater,
    };
  }
  get managerRouteName(): string | undefined {
    return this.inactiveManager
      ? undefined /* If the RouteName is undefined, the manager chip will be inactive */
      : this.routes.MANAGER_ABOUT;
  }
  get postedAt(): string[] {
    return getPostedAt(this.posting.public);
  }
  get getBudet(): string {
    let budget = "$0";
    const minrate = this.posting.min_rate;
    const maxrate = this.posting.max_rate;
    if (minrate != null) {
      const isDecimal = `${minrate}`.match(/\./g);

      budget = formatCurrency(minrate, { precision: isDecimal ? 2 : 0 });
    }
    if (maxrate != null) {
      const isDecimal = `${maxrate}`.match(/\./g);
      budget += `- ${formatCurrency(maxrate, {
        precision: isDecimal ? 2 : 0,
      })}`;
    }
    // return formatCurrency(this.posting.estimate_rate, { precision: 2 });
    return budget;
  }
  get manager(): any {
    const user = this.posting.manager?.user;
    if (!user) {
      return undefined;
    }

    return {
      name: `${user.given_name} ${user.family_name}`,
      id: this.posting.manager!.id,
      avatar: user.avatar,
    };
  }

	get managerRoute() {
		return {
				name: RouteNames.MANAGER_ABOUT,
				params: {
					id: this.manager.id,
			},
		};
	}

  get initial(): string {
    const firstName = this.posting.manager.user.given_name
    const lastName = this.posting.manager.user.family_name;

    if (!firstName && !lastName) {
      return "";
    }

    const givenName = firstName ? firstName.trimLeft()[0] : "";
    const familyName = lastName ? lastName.trimLeft()[0] : "";
    return `${givenName}${familyName}`;
  }

  get isFullTimeJob(): boolean {
    return this.posting.employment_type?.toLowerCase()?.includes("full time");
  }

  get startDate(): string {
    return this.posting.start_date ? this.posting.start_date : "n/a";
  }

  get endDate(): string {
    return this.posting.end_date ? this.posting.end_date : "n/a";
  }

  get bgStyle() {
    return {
      background: hslColorFromName(this.initial || ""),
    };
  }

  get badgeData(): { value: string; color: string; class?: string } {
    if (!this.posting.active) {
      return {
        value: "Inactive",
        color: BadgeColor.WARNING,
      };
    }
    if (this.posting.active) {
      return {
        value: "Active",
        color: BadgeColor.SUCCESS,
        class: "text-badge",
      };
    }
    if (!this.applications.length) {
      return {
        value: "No applicants yet",
        color: BadgeColor.INFO,
      };
    }
    const applicants =
      this.applications.length == 1 ? "applicant" : "applicants";
    return {
      value: `${this.applications.length} ${applicants}`,
      color: BadgeColor.SUCCESS,
    };
  }
  get status(): string {
    return getStatusLabel(this.posting.active);
  }

  get postingDetailsRouteName(): string {
    return this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_DETAILS);
  }

  onEdit() {
    this.$emit("post:edit", this.posting);
  }
  get activeButtonText(): string {
    return this.posting.active ? "Deactivate" : "Activate";
  }
  get type(): string | undefined {
    return this.posting.employment_type || undefined;
  }
  async handleToggleActiveness(): Promise<void> {
    this.processing = true;
    this.$emit("post:toggleStart", this.posting);
    this.$apollo
      .mutate({
        mutation: UPDATE_POSTING_ACTIVE_STATUS,
        variables: {
          id: this.posting.id,
          set: { active: !this.posting.active },
        },
      })
      .then((d) => {
        this.$emit("post:toggleSuccess", this.posting);
      })
      .catch((e) => {
        this.$emit("post:toggleError", this.posting);
      })
      .finally(() => {
        this.$emit("post:toggleEnd", this.posting);
        this.processing = false;
      });
  }

  get location(): string {
    const location = getLocationInfo(this.posting);
    return location;
  }
  get getLocationClasses() {
    const location = this.location;
    const arr = (location || "").split(" ");
    const isGreater = arr.find((a) => a.length >= 50);

    return {
      custom_header_class: isGreater,
    };
  }
}
